import { Spin } from "antd";
import type { LineConfig } from "./LineChartCard";
import styles from "./GapAnalysisChartCard.module.css";

export interface GapAnalysisChartCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  lines: LineConfig[];
  referenceKey?: string;
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
}

interface GapSeries {
  key: string;
  label: string;
  color: string;
  gaps: number[];
  firstGap: number;
  lastGap: number;
  gapChange: number;
  firstValue: number;
  lastValue: number;
}

const CHART_WIDTH = 820;
const CHART_HEIGHT = 260;
const CHART_PADDING = {
  top: 26,
  right: 34,
  bottom: 42,
  left: 48,
};

const getNumericValue = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value: number, suffix = "%") => {
  const formattedValue = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(1).replace(/\.0$/, "");

  return `${formattedValue}${suffix}`;
};

const formatGap = (value: number) => {
  const sign = value > 0 ? "+" : "";
  const formattedValue = Math.abs(value) < 0.05
    ? "0"
    : value.toFixed(1).replace(/\.0$/, "");

  return `${sign}${formattedValue} pp`;
};

const describeGap = (value: number, referenceLabel: string) => {
  if (Math.abs(value) < 0.05) return `alineat amb ${referenceLabel}`;
  return value > 0 ? `per sobre de ${referenceLabel}` : `per sota de ${referenceLabel}`;
};

const buildGapSeries = (
  data: Record<string, string | number>[],
  lines: LineConfig[],
  referenceKey: string
): GapSeries[] => {
  const referenceLine = lines.find((line) => line.key === referenceKey);
  if (!referenceLine) return [];

  return lines
    .filter((line) => line.key !== referenceKey)
    .map((line) => {
      const alignedValues = data
        .map((point) => {
          const referenceValue = getNumericValue(point[referenceKey]);
          const currentValue = getNumericValue(point[line.key]);

          if (referenceValue === null || currentValue === null) return null;

          return {
            currentValue,
            gap: currentValue - referenceValue,
          };
        })
        .filter((value): value is { currentValue: number; gap: number } => value !== null);

      if (alignedValues.length === 0) return null;

      const gaps = alignedValues.map((value) => value.gap);
      const firstGap = gaps[0];
      const lastGap = gaps[gaps.length - 1];

      return {
        key: line.key,
        label: line.label,
        color: line.color,
        gaps,
        firstGap,
        lastGap,
        gapChange: lastGap - firstGap,
        firstValue: alignedValues[0].currentValue,
        lastValue: alignedValues[alignedValues.length - 1].currentValue,
      };
    })
    .filter((series): series is GapSeries => series !== null);
};

const getY = (value: number, maxAbsGap: number) => {
  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const ratio = maxAbsGap === 0 ? 0.5 : (value + maxAbsGap) / (maxAbsGap * 2);

  return plotBottom - ratio * plotHeight;
};

const getX = (index: number, count: number) => {
  const plotLeft = CHART_PADDING.left;
  const plotRight = CHART_WIDTH - CHART_PADDING.right;
  const plotWidth = plotRight - plotLeft;

  if (count <= 1) return plotLeft + plotWidth / 2;

  return plotLeft + (index / (count - 1)) * plotWidth;
};

const getBadgeClass = (value: number) => {
  if (value > 0.05) return styles.badgeUp;
  if (value < -0.05) return styles.badgeDown;
  return styles.badgeFlat;
};

const GapAnalysisChartCard = ({
  title,
  data,
  xKey,
  lines,
  referenceKey = "Catalunya",
  loading = false,
  error = null,
  headerExtra,
}: GapAnalysisChartCardProps) => {
  const referenceLine = lines.find((line) => line.key === referenceKey);
  const referenceLabel = referenceLine?.label ?? referenceKey;
  const referenceValues = data
    .map((point) => getNumericValue(point[referenceKey]))
    .filter((value): value is number => value !== null);
  const referenceFirst = referenceValues[0];
  const referenceLast = referenceValues[referenceValues.length - 1];
  const referenceChange = referenceValues.length > 1
    ? referenceLast - referenceFirst
    : 0;
  const gapSeries = buildGapSeries(data, lines, referenceKey);
  const allGaps = gapSeries.flatMap((series) => series.gaps);
  const maxAbsGap = Math.max(1, ...allGaps.map((gap) => Math.abs(gap)));
  const firstPeriod = data[0]?.[xKey];
  const lastPeriod = data[data.length - 1]?.[xKey];
  const widestGap = gapSeries.reduce<GapSeries | null>((current, series) => {
    if (!current) return series;
    return Math.abs(series.lastGap) > Math.abs(current.lastGap) ? series : current;
  }, null);
  const ticks = [maxAbsGap, 0, -maxAbsGap];
  const yearLabels = data.map((point) => String(point[xKey]));

  return (
    <div className={styles.card} data-chart-card>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {headerExtra}
      </div>

      {loading && (
        <div className={styles.centered}>
          <Spin size="small" /> Carregant dades…
        </div>
      )}
      {!loading && error && (
        <div className={styles.centered}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {!loading && !error && (gapSeries.length === 0 || referenceValues.length === 0) && (
        <div className={styles.centered}>
          <p className={styles.emptyMessage}>Sense dades disponibles</p>
        </div>
      )}
      {!loading && !error && gapSeries.length > 0 && referenceValues.length > 0 && (
        <div className={styles.content}>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <div className={styles.summaryLabel}>{referenceLabel} {lastPeriod}</div>
              <div className={styles.summaryValue}>{formatValue(referenceLast)}</div>
              <div className={styles.summaryHint}>
                {formatGap(referenceChange)} des de {firstPeriod}
              </div>
            </div>
            {widestGap && (
              <div className={styles.summaryItem}>
                <div className={styles.summaryLabel}>Bretxa principal</div>
                <div
                  className={`${styles.summaryValue} ${
                    widestGap.lastGap > 0.05
                      ? styles.positive
                      : widestGap.lastGap < -0.05
                        ? styles.negative
                        : styles.neutral
                  }`}
                >
                  {formatGap(widestGap.lastGap)}
                </div>
                <div className={styles.summaryHint}>
                  {widestGap.label} {describeGap(widestGap.lastGap, referenceLabel)}
                </div>
              </div>
            )}
            <div className={styles.summaryItem}>
              <div className={styles.summaryLabel}>Lectura</div>
              <div className={styles.summaryValue}>
                {widestGap && Math.abs(widestGap.lastGap) >= 0.05 ? "Diferència" : "Alineat"}
              </div>
              <div className={styles.summaryHint}>
                Valors expressats com a bretxa vs {referenceLabel}
              </div>
            </div>
          </div>

          <div className={styles.chartPanel}>
            <div className={styles.chartHeader}>
              <div className={styles.chartTitle}>Evolució de la bretxa contra {referenceLabel}</div>
              <div className={styles.legend}>
                {gapSeries.map((series) => (
                  <span key={series.key} className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ backgroundColor: series.color }}
                    />
                    {series.label} - {referenceLabel}
                  </span>
                ))}
              </div>
            </div>
            <svg
              className={styles.gapChart}
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              role="img"
              aria-label={`Bretxa de ${gapSeries.map((series) => series.label).join(" i ")} contra ${referenceLabel}`}
            >
              {ticks.map((tick) => (
                <g key={tick}>
                  <line
                    className={tick === 0 ? styles.zeroLine : styles.gridLine}
                    x1={CHART_PADDING.left}
                    y1={getY(tick, maxAbsGap)}
                    x2={CHART_WIDTH - CHART_PADDING.right}
                    y2={getY(tick, maxAbsGap)}
                  />
                  <text
                    className={styles.axisLabel}
                    x={CHART_PADDING.left - 10}
                    y={getY(tick, maxAbsGap) + 4}
                    textAnchor="end"
                  >
                    {formatGap(tick)}
                  </text>
                </g>
              ))}
              <text
                className={styles.gapLabel}
                x={CHART_WIDTH - CHART_PADDING.right}
                y={getY(0, maxAbsGap) - 8}
                textAnchor="end"
              >
                {referenceLabel}
              </text>
              {gapSeries.map((series) => {
                const points = series.gaps
                  .map((gap, index) => `${getX(index, series.gaps.length)},${getY(gap, maxAbsGap)}`)
                  .join(" ");

                return (
                  <g key={series.key}>
                    <polyline
                      className={styles.gapLine}
                      points={points}
                      stroke={series.color}
                    />
                    {series.gaps.map((gap, index) => (
                      <circle
                        key={`${series.key}-${index}`}
                        className={styles.gapDot}
                        cx={getX(index, series.gaps.length)}
                        cy={getY(gap, maxAbsGap)}
                        r="4"
                        fill={series.color}
                      />
                    ))}
                  </g>
                );
              })}
              {yearLabels.map((year, index) => (
                <text
                  key={year}
                  className={styles.yearLabel}
                  x={getX(index, yearLabels.length)}
                  y={CHART_HEIGHT - 12}
                  textAnchor="middle"
                >
                  {year}
                </text>
              ))}
            </svg>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Sistema</th>
                  <th>{lastPeriod}</th>
                  <th>Bretxa final</th>
                  <th>Canvi de bretxa</th>
                  <th>Lectura</th>
                </tr>
              </thead>
              <tbody>
                {gapSeries.map((series) => (
                  <tr key={series.key}>
                    <td>
                      <span className={styles.systemName}>
                        <span
                          className={styles.legendDot}
                          style={{ backgroundColor: series.color }}
                        />
                        {series.label}
                      </span>
                    </td>
                    <td>{formatValue(series.lastValue)}</td>
                    <td>
                      <span className={`${styles.badge} ${getBadgeClass(series.lastGap)}`}>
                        {formatGap(series.lastGap)}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.badge} ${getBadgeClass(series.gapChange)}`}>
                        {formatGap(series.gapChange)}
                      </span>
                    </td>
                    <td>{describeGap(series.lastGap, referenceLabel)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GapAnalysisChartCard;
